/* global google */
// Complete ApiService with optimized token storage - Cleaned version

import { getEnv } from "../config/env";
import { resolveQueryThreshold } from "../utils";

class ApiService {
	constructor() {
		const env = getEnv();

		this.proxyUrl = env.REACT_APP_PROXY;
		this.apiUrl = this.proxyUrl;
		this.timeout = 300000;
		this.tokenChangeListeners = [];

		this.TOKEN_STORAGE_KEY = "google_id_token";
		this.REFRESH_TOKEN_KEY = "google_refresh_token";
		this.tokens = {
			idToken: null,
			accessToken: null,
			refreshToken: null,
			csrfToken: null,
			expiresAt: null,
			refreshExpiresAt: null,
		};

		this.tokenClient = null;
		this.idTokenClient = null;
		this.isInitializing = false;
		this.isInitialized = false;

		// Cache for domain filters
		this.filtersCache = new Map();
		this.filtersCacheTimeout = 10 * 60 * 1000;

		// Track ongoing requests to prevent duplicate calls
		this.ongoingRequests = new Map();

		// 🔍 DEBUG: Call tracking
		this.debugMode = false; // Set to false to disable debug logs
		this.callCounts = new Map();
		this.callStacks = new Map();

		this.loadStoredToken();
		this.initializeGoogleIdentityServices();

		this.authFailureCallback = null;
		this.isHandlingAuthFailure = false;
	}

	logDebug(method, message, data = null) {
		if (!this.debugMode) return true;

		const timestamp = new Date().toISOString().split("T")[1].slice(0, -1);
		const count = (this.callCounts.get(method) || 0) + 1;
		this.callCounts.set(method, count);

		const logStyle = "color: #00ff00; font-weight: bold";
		console.log(
			`%c[ApiService ${timestamp}] ${method} (Call #${count}): ${message}`,
			logStyle,
			data || "",
		);

		// Track call counts for debugging (no blocking)
		if (count > 10) {
			console.warn(
				`⚠️ High call count detected in ${method}! Called ${count} times.`,
			);
			console.trace("Call stack:");
		}

		return true; // Always continue
	}

	resetDebugCounters() {
		console.log("🔄 Resetting debug counters");
		this.callCounts.clear();
		this.callStacks.clear();
	}

	// ==================== TOKEN CHANGE LISTENERS ====================

	onTokenChange(callback) {
		this.tokenChangeListeners.push(callback);
		return () => {
			this.tokenChangeListeners = this.tokenChangeListeners.filter(
				(cb) => cb !== callback,
			);
		};
	}

	onAuthFailure(callback) {
		this.authFailureCallback = callback;
	}

	handleAuthFailure(error) {
		this.logDebug(
			"handleAuthFailure",
			`Called - isHandlingAuthFailure: ${this.isHandlingAuthFailure}`,
		);

		if (this.isHandlingAuthFailure) {
			this.logDebug(
				"handleAuthFailure",
				"⏭️ SKIPPED - Already handling auth failure",
			);
			return;
		}

		this.isHandlingAuthFailure = true;
		console.error("Authentication failed:", error.message);

		// Clear tokens
		this.clearStoredToken();

		// Trigger callback if registered
		if (this.authFailureCallback) {
			this.logDebug("handleAuthFailure", "📞 Calling authFailureCallback");
			try {
				this.authFailureCallback(error);
			} catch (callbackError) {
				console.error("Auth failure callback error:", callbackError);
			}
		}

		// Reset flag after a delay
		setTimeout(() => {
			this.isHandlingAuthFailure = false;
			this.logDebug("handleAuthFailure", "🔓 Reset isHandlingAuthFailure flag");
		}, 1000);
	}

	notifyTokenChange(event) {
		this.logDebug(
			"notifyTokenChange",
			`Event: ${event}, Listeners: ${this.tokenChangeListeners.length}`,
		);

		this.tokenChangeListeners.forEach((callback, index) => {
			try {
				this.logDebug("notifyTokenChange", `Calling listener #${index + 1}`);
				callback(event, {
					isAuthenticated: this.isAuthenticated(),
					user: this.getUser(),
				});
			} catch (error) {
				console.error("Token change listener error:", error);
			}
		});
	}

	// ==================== CORE TOKEN STORAGE ====================

	storeToken(token) {
		if (!token || typeof token !== "string" || !token.trim()) {
			console.error("Invalid token provided");
			return false;
		}

		try {
			// Store in memory
			this.tokens.idToken = token;

			// Set expiration
			const payload = this.parseJWT(token);
			if (payload?.exp) {
				this.tokens.expiresAt = payload.exp * 1000;
			}

			// Store in localStorage
			localStorage.setItem(this.TOKEN_STORAGE_KEY, token);

			// Verify storage
			const stored = localStorage.getItem(this.TOKEN_STORAGE_KEY);
			if (stored !== token) {
				throw new Error("Storage verification failed");
			}

			this.notifyTokenChange("stored");
			return true;
		} catch (error) {
			console.error("Token storage failed:", error.message);

			// Fallback to sessionStorage
			try {
				sessionStorage.setItem(this.TOKEN_STORAGE_KEY, token);
				return true;
			} catch (fallbackError) {
				console.error("All storage methods failed");
				return false;
			}
		}
	}

	storeTokens(tokenResponse) {
		try {
			// Store access token
			if (tokenResponse.access_token) {
				this.tokens.accessToken = tokenResponse.access_token;
				this.tokens.expiresAt = Date.now() + tokenResponse.expires_in * 1000;
			}

			// Store refresh token
			if (tokenResponse.refresh_token) {
				this.tokens.refreshToken = tokenResponse.refresh_token;
				localStorage.setItem(
					this.REFRESH_TOKEN_KEY,
					tokenResponse.refresh_token,
				);
				this.tokens.refreshExpiresAt = Date.now() + 180 * 24 * 60 * 60 * 1000; // 180 days
			}

			// Store ID token if provided
			if (tokenResponse.id_token) {
				this.tokens.idToken = tokenResponse.id_token;
				localStorage.setItem(this.TOKEN_STORAGE_KEY, tokenResponse.id_token);
			}

			this.notifyTokenChange("stored");
			return true;
		} catch (error) {
			console.error("Token storage failed:", error);
			return false;
		}
	}

	loadStoredToken() {
		try {
			// Try localStorage first
			let token = localStorage.getItem(this.TOKEN_STORAGE_KEY);

			// Fallback to sessionStorage
			if (!token) {
				token = sessionStorage.getItem(this.TOKEN_STORAGE_KEY);
			}

			if (token && token.trim()) {
				this.tokens.idToken = token;

				if (this.isValidJWT(token)) {
					const payload = this.parseJWT(token);
					if (payload?.exp) {
						this.tokens.expiresAt = payload.exp * 1000;
					}
				}
			}

			// Load refresh token
			const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
			if (refreshToken) {
				this.tokens.refreshToken = refreshToken;
				this.tokens.refreshExpiresAt = Date.now() + 180 * 24 * 60 * 60 * 1000;
			}
		} catch (error) {
			console.error("Error loading token:", error.message);
			this.clearStoredToken();
		}
	}

	clearStoredToken() {
		try {
			localStorage.removeItem(this.TOKEN_STORAGE_KEY);
			localStorage.removeItem(this.REFRESH_TOKEN_KEY);
			sessionStorage.removeItem(this.TOKEN_STORAGE_KEY);
			document.cookie = `${this.TOKEN_STORAGE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
		} catch (error) {
			console.warn("Error clearing storage:", error.message);
		}

		this.tokens.idToken = null;
		this.tokens.accessToken = null;
		this.tokens.refreshToken = null;
		this.tokens.expiresAt = null;
		this.tokens.refreshExpiresAt = null;

		this.notifyTokenChange("cleared");
	}

	// ==================== TOKEN VALIDATION ====================

	isValidJWT(token) {
		return token && typeof token === "string" && token.split(".").length === 3;
	}

	isIdTokenValid() {
		if (!this.tokens.idToken) return false;

		try {
			const payload = this.parseJWT(this.tokens.idToken);
			if (!payload?.exp) return false;

			return payload.exp * 1000 > Date.now();
		} catch {
			return false;
		}
	}

	isAccessTokenValid() {
		if (!this.tokens.accessToken || !this.tokens.expiresAt) return false;
		const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
		return this.tokens.expiresAt > fiveMinutesFromNow;
	}

	setProxyUrl(url) {
		const normalized = url.replace(/\/$/, "");
		this.proxyUrl = normalized;
		this.apiUrl = normalized;
	}

	isAuthenticated() {
		if (this.isIdTokenValid() || this.isAccessTokenValid()) return true;
		// Plain bearer token with no expiry info — treat as authenticated if present
		return !!(this.tokens.idToken && this.tokens.idToken.trim());
	}

	parseJWT(token) {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	getUser() {
		if (this.tokens.idToken && this.isIdTokenValid()) {
			const payload = this.parseJWT(this.tokens.idToken);
			return payload
				? {
						id: payload.sub,
						email: payload.email,
						name: payload.name,
						picture: payload.picture,
						email_verified: payload.email_verified,
					}
				: null;
		}
		return null;
	}

	// ==================== GOOGLE IDENTITY SERVICES ====================

	async initializeGoogleIdentityServices() {
		if (this.isInitializing || this.isInitialized) return;
		this.isInitializing = true;

		try {
			await this.waitForGoogleIdentityServices();

			if (!this.googleClientId) {
				console.warn("Google Client ID not configured");
				this.isInitializing = false;
				return;
			}

			google.accounts.id.initialize({
				client_id: this.googleClientId,
				callback: this.handleIdTokenResponse.bind(this),
				auto_select: false,
				cancel_on_tap_outside: true,
				itp_support: true,
				ux_mode: "popup",
			});

			this.tokenClient = google.accounts.oauth2.initTokenClient({
				client_id: this.googleClientId,
				scope: this.googleScopes,
				callback: this.handleOAuthTokenResponse.bind(this),
				access_type: "offline",
				prompt: "consent",
			});

			this.generateCSRFToken();
			this.isInitialized = true;
		} catch (error) {
			console.error("Failed to initialize Google Identity Services:", error);
		} finally {
			this.isInitializing = false;
		}
	}

	async waitForGoogleIdentityServices() {
		return new Promise((resolve, reject) => {
			let attempts = 0;
			const maxAttempts = 50;

			const checkGoogle = () => {
				attempts++;
				if (typeof window !== "undefined" && window.google?.accounts) {
					resolve();
				} else if (attempts >= maxAttempts) {
					reject(new Error("Google Identity Services failed to load"));
				} else {
					setTimeout(checkGoogle, 100);
				}
			};
			checkGoogle();
		});
	}

	// ==================== SIGN IN/OUT ====================

	async signIn() {
		try {
			if (!this.isInitialized) {
				await this.initializeGoogleIdentityServices();
			}

			const idTokenResponse = await this.requestIdToken();
			if (idTokenResponse && this.tokens.idToken) {
				this.storeToken(this.tokens.idToken);
				return {
					success: true,
					method: "id_token",
					token: this.tokens.idToken,
					user: this.getUser(),
				};
			}

			throw new Error("ID token request failed");
		} catch (error) {
			console.error("Sign-in failed:", error);
			return {
				success: false,
				error: error.message,
			};
		}
	}

	async requestIdToken() {
		return new Promise((resolve, reject) => {
			this.idTokenPromise = { resolve, reject };

			google.accounts.id.prompt((notification) => {
				if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
					reject(new Error("One Tap not available"));
				}
			});
		});
	}

	async requestAccessToken() {
		return new Promise((resolve, reject) => {
			this.tokenRequestPromise = { resolve, reject };
			this.tokenClient.requestAccessToken();
		});
	}

	handleIdTokenResponse(response) {
		try {
			if (response.error) {
				console.error("ID token error:", response.error);
				this.idTokenPromise?.reject(new Error(response.error));
				return;
			}

			const idToken = response.credential;
			if (!idToken) {
				this.idTokenPromise?.reject(new Error("No credential received"));
				return;
			}

			const success = this.storeToken(idToken);

			if (success) {
				const user = this.getUser();
				this.idTokenPromise?.resolve({
					success: true,
					credential: idToken,
					user: user,
				});
			} else {
				this.idTokenPromise?.reject(new Error("Failed to store token"));
			}
		} catch (error) {
			console.error("Error handling ID token:", error);
			this.idTokenPromise?.reject(error);
		} finally {
			this.idTokenPromise = null;
		}
	}

	handleOAuthTokenResponse(response) {
		try {
			if (response.error) {
				console.error("OAuth error:", response.error);
				this.tokenRequestPromise?.reject(new Error(response.error));
				return;
			}

			const tokenData = {
				access_token: response.access_token,
				expires_in: response.expires_in,
				refresh_token: response.refresh_token,
				id_token: response.id_token,
			};

			this.storeTokens(tokenData);
			this.tokenRequestPromise?.resolve(response);
		} catch (error) {
			console.error("Error handling OAuth token:", error);
			this.tokenRequestPromise?.reject(error);
		} finally {
			this.tokenRequestPromise = null;
		}
	}

	signOut() {
		this.clearStoredToken();
		this.isHandlingAuthFailure = false;

		if (
			typeof google !== "undefined" &&
			google.accounts?.id?.disableAutoSelect
		) {
			google.accounts.id.disableAutoSelect();
		}

		return { success: true };
	}

	// ==================== HEADERS & REQUESTS ====================

	getHeaders() {
		const headers = {
			Accept: "application/json",
			"Content-Type": "application/json",
		};

		if (this.tokens?.idToken && this.isIdTokenValid()) {
			headers["Authorization"] = `Bearer ${this.tokens.idToken}`;
		} else if (this.tokens?.accessToken && this.isAccessTokenValid()) {
			headers["Authorization"] = `Bearer ${this.tokens.accessToken}`;
		} else if (this.tokens?.idToken) {
			headers["Authorization"] = `Bearer ${this.tokens.idToken}`;
		}

		return headers;
	}

	// ==================== REFRESH TOKEN METHODS ====================

	async refreshToken() {
		try {
			// Strategy 1: Try silent access token refresh
			try {
				const silentResult = await this.attemptSilentTokenRefresh();
				if (silentResult.success) {
					return silentResult;
				}
			} catch (silentError) {
				// Continue to next strategy
			}

			// Strategy 2: Try Google One Tap refresh
			try {
				const oneTapResult = await this.attemptOneTapRefresh();
				if (oneTapResult.success) {
					return oneTapResult;
				}
			} catch (oneTapError) {
				// Continue to fallback
			}

			// Strategy 3: Fallback to sign-in
			const result = await this.signIn();

			if (result.success) {
				return result;
			} else {
				throw new Error("All refresh strategies failed: " + result.error);
			}
		} catch (error) {
			console.error("Smart refresh failed:", error);
			throw error;
		}
	}

	async attemptSilentTokenRefresh() {
		return new Promise((resolve, reject) => {
			try {
				const silentClient = google.accounts.oauth2.initTokenClient({
					client_id: this.googleClientId,
					scope: this.googleScopes,
					prompt: "none",
					callback: (response) => {
						if (response.error) {
							if (response.error === "interaction_required") {
								resolve({ success: false, reason: "interaction_required" });
							} else {
								reject(new Error(response.error));
							}
						} else if (response.access_token) {
							this.storeTokens({
								access_token: response.access_token,
								expires_in: response.expires_in || 3600,
								id_token: response.id_token,
							});

							resolve({
								success: true,
								method: "silent_refresh",
								token: response.access_token,
							});
						} else {
							resolve({ success: false, reason: "no_token_received" });
						}
					},
				});

				silentClient.requestAccessToken();

				// Timeout after 5 seconds
				setTimeout(() => {
					resolve({ success: false, reason: "timeout" });
				}, 5000);
			} catch (error) {
				reject(error);
			}
		});
	}

	async attemptOneTapRefresh() {
		return new Promise((resolve, reject) => {
			try {
				google.accounts.id.prompt((notification) => {
					if (notification.isDisplayed()) {
						// Continue with One Tap
					} else if (notification.isNotDisplayed()) {
						resolve({ success: false, reason: "not_displayed" });
					} else if (notification.isSkippedMoment()) {
						resolve({ success: false, reason: "skipped" });
					}
				});

				const originalPromise = this.idTokenPromise;
				this.idTokenPromise = {
					resolve: (result) => {
						this.idTokenPromise = originalPromise;
						resolve({ success: true, method: "one_tap", ...result });
					},
					reject: (error) => {
						this.idTokenPromise = originalPromise;
						resolve({ success: false, reason: error.message });
					},
				};

				// Timeout after 10 seconds
				setTimeout(() => {
					if (this.idTokenPromise !== originalPromise) {
						this.idTokenPromise = originalPromise;
						resolve({ success: false, reason: "timeout" });
					}
				}, 10000);
			} catch (error) {
				reject(error);
			}
		});
	}

	isTokenExpiringSoon(bufferMinutes = 5) {
		if (!this.tokens.expiresAt) return true;

		const bufferMs = bufferMinutes * 60 * 1000;
		const timeUntilExpiry = this.tokens.expiresAt - Date.now();

		return timeUntilExpiry <= bufferMs;
	}

	async ensureValidToken() {
		this.logDebug("ensureValidToken", "START");

		try {
			// Plain bearer token with no known expiry — use as-is, skip Google refresh
			if (this.tokens.idToken && !this.tokens.expiresAt) {
				this.logDebug("ensureValidToken", "✅ Plain bearer token, skipping refresh");
				return true;
			}

			// If token is valid and not expiring soon, use it
			if (this.isAuthenticated() && !this.isTokenExpiringSoon()) {
				this.logDebug("ensureValidToken", "✅ Token is valid and not expiring");
				return true;
			}

			this.logDebug("ensureValidToken", "⚠️ Token invalid or expiring soon");

			// If already refreshing, wait for completion
			if (this.isRefreshing && this.refreshPromise) {
				this.logDebug("ensureValidToken", "⏳ Already refreshing, waiting...");
				try {
					await this.refreshPromise;
					const isAuth = this.isAuthenticated();
					this.logDebug("ensureValidToken", `After waiting: isAuth=${isAuth}`);
					return isAuth;
				} catch (error) {
					this.logDebug("ensureValidToken", `Wait failed: ${error.message}`);
					// Continue to new refresh attempt
				}
			}

			// Start refresh process
			this.logDebug("ensureValidToken", "🔄 Starting refresh process");
			this.isRefreshing = true;
			this.refreshPromise = this.refreshToken();

			try {
				const result = await this.refreshPromise;
				this.logDebug("ensureValidToken", `Refresh result: ${result.success}`);
				return result.success;
			} catch (error) {
				this.logDebug(
					"ensureValidToken",
					`❌ Refresh failed: ${error.message}`,
				);
				return false;
			} finally {
				this.isRefreshing = false;
				this.refreshPromise = null;
				this.logDebug("ensureValidToken", "Refresh process completed");
			}
		} catch (error) {
			this.logDebug("ensureValidToken", `❌ ERROR: ${error.message}`);
			return false;
		}
	}

	hasValidRefreshToken() {
		return false; // Google Identity Services doesn't provide refresh tokens in browser
	}

	// ==================== FETCH WITH FALLBACK (FIXED) ====================

	async fetchWithFallback(endpoint, options = {}) {
		const shouldContinue = this.logDebug(
			"fetchWithFallback",
			`START - Endpoint: ${endpoint}`,
		);
		if (shouldContinue === false) {
			return new Response(
				JSON.stringify({ error: "Emergency stop - too many calls" }),
				{
					status: 500,
					headers: { "Content-Type": "application/json" },
				},
			);
		}

		const requestId = `${endpoint}-${Date.now()}`;

		const maxRetries = 1; // REDUCED from 2 to 1 to prevent infinite loops
		let attempt = 0;

		while (attempt < maxRetries) {
			try {
				this.logDebug(
					"fetchWithFallback",
					`Attempt ${attempt + 1}/${maxRetries}`,
					{ endpoint },
				);

				const hasValidToken = await this.ensureValidToken();
				this.logDebug("fetchWithFallback", `Has valid token: ${hasValidToken}`);

				if (!hasValidToken && attempt === 0) {
					this.logDebug(
						"fetchWithFallback",
						"❌ NO VALID TOKEN - Triggering auth failure",
					);
					// Only try to refresh on first attempt
					this.handleAuthFailure(
						new Error("Authentication failed - unable to obtain valid token"),
					);
					return new Response(
						JSON.stringify({ error: "Authentication failed" }),
						{
							status: 401,
							headers: { "Content-Type": "application/json" },
						},
					);
				}

				const headers = this.getHeaders();

				const requestOptions = {
					method: "GET",
					...options,
					headers: { ...headers, ...options.headers },
					mode: "cors",
					credentials: "omit",
					cache: "no-cache",
				};

				const normalizedEndpoint = endpoint.startsWith("/")
					? endpoint
					: `/${endpoint}`;
				const url = `${this.apiUrl}${normalizedEndpoint}`;

				this.logDebug("fetchWithFallback", `📡 Fetching: ${url}`);

				const response = await fetch(url, {
					...requestOptions,
					signal: AbortSignal.timeout(this.timeout),
				});

				this.logDebug(
					"fetchWithFallback",
					`Response: ${response.status} ${response.statusText}`,
				);

				// DON'T retry on 401/403 - just fail immediately to prevent loops
				if (response.status === 401 || response.status === 403) {
					this.logDebug(
						"fetchWithFallback",
						"🚫 AUTH ERROR - Not retrying to prevent loop",
					);
					this.handleAuthFailure(
						new Error("Authentication failed - invalid credentials"),
					);
					return new Response(
						JSON.stringify({ error: "Authentication failed" }),
						{
							status: 401,
							headers: { "Content-Type": "application/json" },
						},
					);
				}

				this.logDebug("fetchWithFallback", "✅ SUCCESS");
				return response;
			} catch (error) {
				this.logDebug("fetchWithFallback", `❌ ERROR: ${error.message}`);

				if (attempt === maxRetries - 1) {
					this.logDebug("fetchWithFallback", "🛑 MAX RETRIES REACHED");
					// Last attempt failed - don't call handleAuthFailure for network errors
					if (!error.message.includes("Authentication failed")) {
						throw error;
					}
					this.handleAuthFailure(error);
					return new Response(JSON.stringify({ error: "Request failed" }), {
						status: 500,
						headers: { "Content-Type": "application/json" },
					});
				}

				attempt++;
				this.logDebug("fetchWithFallback", `⏱️ Waiting 1s before retry`);
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}
		}
	}

	// ==================== UTILITY METHODS ====================

	generateCSRFToken() {
		try {
			const array = new Uint8Array(16);
			crypto.getRandomValues(array);
			this.tokens.csrfToken = Array.from(array, (byte) =>
				byte.toString(16).padStart(2, "0"),
			).join("");
		} catch (error) {
			this.tokens.csrfToken = Math.random().toString(36).substr(2, 16);
		}
	}

	getTokenStatus() {
		return {
			hasToken: !!this.tokens.idToken,
			isValid: this.isIdTokenValid(),
			user: this.getUser()?.email,
			inStorage: !!localStorage.getItem(this.TOKEN_STORAGE_KEY),
		};
	}

	async testConnection() {
		try {
			const response = await this.fetchWithFallback("/health");
			if (response.ok) {
				return { success: true, message: "API connection successful" };
			} else {
				return { success: false, error: `HTTP ${response.status}` };
			}
		} catch (error) {
			return { success: false, error: error.message };
		}
	}

	// ==================== DOMAIN API METHODS (FIXED) ====================

	mapCategoriesToIAB(categories) {
		if (!categories || !Array.isArray(categories) || categories.length === 0) {
			return [];
		}
		return categories.filter(Boolean);
	}

	mapFiltersForAPI(filters) {
		if (!filters) return filters;

		const mappedFilters = { ...filters };

		if (
			filters.categories &&
			Array.isArray(filters.categories) &&
			filters.categories.length > 0
		) {
			mappedFilters.iab_category = this.mapCategoriesToIAB(filters.categories);
		}

		return mappedFilters;
	}

	async getDomainFilterValues() {
		const shouldContinue = this.logDebug("getDomainFilterValues", "START");
		if (shouldContinue === false) {
			return {
				success: false,
				error: "Emergency stop - too many calls",
				data: null,
			};
		}

		const maxRetries = 3;
		const user = this.getUser();
		const cacheKey = user
			? `domain_filter_values_${user.id}`
			: "domain_filter_values_anonymous";

		this.logDebug("getDomainFilterValues", `Cache key: ${cacheKey}`);

		// Check if there's already an ongoing request
		if (this.ongoingRequests.has(cacheKey)) {
			this.logDebug("getDomainFilterValues", "⏳ REUSING ONGOING REQUEST");
			return this.ongoingRequests.get(cacheKey);
		}

		// Check cache first
		const cached = this.filtersCache.get(cacheKey);
		if (cached && Date.now() - cached.timestamp < this.filtersCacheTimeout) {
			this.logDebug("getDomainFilterValues", "✅ RETURNING FROM CACHE", {
				age: Math.floor((Date.now() - cached.timestamp) / 1000) + "s",
			});
			return {
				success: true,
				data: cached.data,
				cached: true,
			};
		}

		this.logDebug("getDomainFilterValues", "🌐 CREATING NEW REQUEST");

		// Create new request promise
		const requestPromise = (async () => {
			let attempt = 0;

			while (attempt < maxRetries) {
				try {
					this.logDebug(
						"getDomainFilterValues",
						`📡 ATTEMPT ${attempt + 1}/${maxRetries}`,
					);

					const response = await this.fetchWithFallback(
						"/domain-filters/get-filter-values?type=site",
					);

					this.logDebug(
						"getDomainFilterValues",
						`Response status: ${response.status}`,
					);

					if (!response.ok) {
						throw new Error(`HTTP ${response.status}: ${response.statusText}`);
					}

					const responseData = await response.json();
					const filterValues = responseData;

					// Ensure categories are properly structured
					if (responseData.domainFilterValues) {
						const iabCategories = responseData.domainFilterValues.iab_category;

						if (iabCategories && Array.isArray(iabCategories)) {
							filterValues.domainFilterValues.categories = iabCategories;
						}
					}

					this.filtersCache.set(cacheKey, {
						data: filterValues,
						timestamp: Date.now(),
					});

					this.logDebug("getDomainFilterValues", "✅ SUCCESS - Cached result");

					return {
						success: true,
						data: filterValues,
						cached: false,
						user: user,
					};
				} catch (error) {
					attempt++;
					this.logDebug(
						"getDomainFilterValues",
						`❌ ATTEMPT ${attempt}/${maxRetries} FAILED: ${error.message}`,
					);

					if (attempt >= maxRetries) {
						this.logDebug("getDomainFilterValues", "🛑 ALL ATTEMPTS EXHAUSTED");
						return {
							success: false,
							error: error.message,
							data: null,
							attemptsExhausted: true,
						};
					}

					// Wait before retrying (exponential backoff)
					const delay = Math.min(1000 * 2 ** (attempt - 1), 5000);
					this.logDebug(
						"getDomainFilterValues",
						`⏱️ Waiting ${delay}ms before retry`,
					);
					await new Promise((resolve) => setTimeout(resolve, delay));
				}
			}
		})();

		// Store the ongoing request
		this.ongoingRequests.set(cacheKey, requestPromise);
		this.logDebug("getDomainFilterValues", "📝 Stored ongoing request");

		try {
			const result = await requestPromise;
			this.logDebug("getDomainFilterValues", "🏁 REQUEST COMPLETED", result);
			return result;
		} finally {
			// Clean up ongoing request
			this.ongoingRequests.delete(cacheKey);
			this.logDebug("getDomainFilterValues", "🧹 Cleaned up ongoing request");
		}
	}

	async getKeywords(keyword) {
		try {
			if (!keyword?.trim()) {
				return { success: false, keywords: [], error: "Empty keyword" };
			}

			const trimmedKeyword = keyword.trim();
			const response = await this.fetchWithFallback(
				`/domains/get-keywords?key=${encodeURIComponent(trimmedKeyword)}&limit=50`,
			);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (Array.isArray(data) && data.length > 0) {
				const searchTermLower = trimmedKeyword.toLowerCase();

				const allKeywords = data.map((item) => {
					const keywordText = Object.keys(item)[0];
					const score = Object.values(item)[0];

					return {
						keyword: keywordText,
						originalKeyword: keywordText,
						score: score,
						relevance: this.calculateRelevance(keywordText, trimmedKeyword),
					};
				});

				const sortedKeywords = allKeywords
					.filter((item) => item.relevance > 0)
					.sort((a, b) => {
						if (b.relevance !== a.relevance) {
							return b.relevance - a.relevance;
						}
						return b.score - a.score;
					})
					.slice(0, 8)
					.map((item) => ({
						keyword: item.originalKeyword,
						text: item.originalKeyword,
						value: item.originalKeyword,
						score: item.score,
						frequency: item.score,
						formattedScore: this.formatScore(item.score),
						badgeClass: this.getScoreBadgeClass(item.score),
						relevance: item.relevance,
						searchVolume: item.score,
						popularityLevel: this.getPopularityLevel(item.score),
						isRelevant: item.relevance >= 70,
					}));

				return {
					success: true,
					keywords: sortedKeywords,
					error: null,
					metadata: {
						searchTerm: trimmedKeyword,
						totalProcessed: allKeywords.length,
						relevantFound: sortedKeywords.length,
						maxScore: Math.max(...sortedKeywords.map((k) => k.score)),
						averageScore: Math.round(
							sortedKeywords.reduce((sum, k) => sum + k.score, 0) /
								sortedKeywords.length,
						),
					},
				};
			}

			// Smart fallback with suggestions
			const fallbackKeywords = [
				`${trimmedKeyword}`,
				`${trimmedKeyword} tips`,
				`${trimmedKeyword} guide`,
				`${trimmedKeyword} news`,
				`best ${trimmedKeyword}`,
				`${trimmedKeyword} review`,
			].map((term, index) => {
				const fallbackScore = 30000 - index * 3000;
				return {
					keyword: term,
					text: term,
					value: term,
					score: fallbackScore,
					frequency: fallbackScore,
					formattedScore: this.formatScore(fallbackScore),
					badgeClass: this.getScoreBadgeClass(fallbackScore),
					relevance: 95 - index * 5,
					searchVolume: fallbackScore,
					popularityLevel: this.getPopularityLevel(fallbackScore),
					isRelevant: true,
					isFallback: true,
				};
			});

			return {
				success: true,
				keywords: fallbackKeywords,
				error: null,
				fallback: true,
				metadata: {
					searchTerm: trimmedKeyword,
					message: "No direct matches found. Showing suggestions.",
				},
			};
		} catch (error) {
			console.error("Enhanced getKeywords error:", error);

			const emergencyKeyword = {
				keyword: keyword.trim(),
				text: keyword.trim(),
				value: keyword.trim(),
				score: 25000,
				frequency: 25000,
				formattedScore: this.formatScore(25000),
				badgeClass: this.getScoreBadgeClass(25000),
				relevance: 100,
				searchVolume: 25000,
				popularityLevel: "Medium",
				isRelevant: true,
				isEmergency: true,
			};

			return {
				success: true,
				keywords: [emergencyKeyword],
				error: error.message,
				fallback: true,
				metadata: {
					searchTerm: keyword.trim(),
					message: "API error occurred. Showing basic suggestion.",
				},
			};
		}
	}

	// Helper methods for keyword processing
	formatScore(score) {
		if (score >= 1000000) return `${(score / 1000000).toFixed(1)}M`;
		if (score >= 100000) return `${(score / 1000).toFixed(0)}K`;
		if (score >= 10000) return `${(score / 1000).toFixed(1)}K`;
		if (score >= 1000) return `${(score / 1000).toFixed(1)}K`;
		return score.toString();
	}

	getScoreBadgeClass(score) {
		if (score >= 80000) return "score-ultra";
		if (score >= 40000) return "score-high";
		if (score >= 15000) return "score-medium";
		if (score >= 5000) return "score-low";
		return "score-minimal";
	}

	calculateRelevance(keywordText, searchTerm) {
		const keyword = keywordText.toLowerCase();
		const search = searchTerm.toLowerCase();

		if (keyword === search) return 100;
		if (keyword.startsWith(search)) return 90;
		if (keyword.includes(search)) return 70;
		if (search.includes(keyword)) return 60;
		return 50;
	}

	getPopularityLevel(score) {
		if (score >= 80000) return "Ultra High";
		if (score >= 40000) return "High";
		if (score >= 15000) return "Medium";
		if (score >= 5000) return "Low";
		return "Minimal";
	}

	// ==================== DOMAIN SEARCH ====================

	async performDomainSearch(
		searchTerms,
		filters,
		activeTab,
		performanceMetric,
		minValue,
		customPayload = null,
	) {
		const maxRetries = 2;
		let attempt = 0;

		while (attempt < maxRetries) {
			try {
				const validMinValue = minValue || "1000";
				let payload;

				if (customPayload) {
					payload = customPayload;
				} else {
					const mappedFilters = this.mapUIFiltersToBackend(filters);
					payload = this.buildSearchPayload(
						searchTerms,
						this.mapFiltersForAPI(filters),
						activeTab,
						performanceMetric,
						validMinValue,
					);
				}

				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), 90000);

				const response = await this.fetchWithFallback("/domains/search", {
					method: "POST",
					body: JSON.stringify(payload),
					signal: controller.signal,
				});

				clearTimeout(timeoutId);

				if (!response.ok) {
					const errorText = await response.text();
					throw new Error(`HTTP ${response.status}: ${errorText}`);
				}

				const data = await response.json();

				if (data.statusCode === "OK" || response.status === 200) {
					return {
						success: true,
						data: data,
						error: null,
					};
				} else if (response.status === 204) {
					return {
						success: true,
						data: { message: "No domain details found" },
						error: null,
					};
				} else {
					throw new Error(
						typeof data === "string" ? data : data.body || "Search failed",
					);
				}
			} catch (error) {
				if (attempt < maxRetries - 1) {
					const delay = (attempt + 1) * 2000;
					await new Promise((resolve) => setTimeout(resolve, delay));
					attempt++;
					continue;
				}

				return {
					success: false,
					data: null,
					error: `Search failed after ${maxRetries} attempts: ${error.message}`,
				};
			}
		}
	}

	// ==================== PAYLOAD BUILDING ====================

	buildSearchPayload(
		searchTerms,
		filters,
		activeTab,
		performanceMetric,
		minValue,
	) {
		const includeTerms = (searchTerms || []).filter(
			(term) => term?.type === "include",
		);
		const excludeTerms = (searchTerms || []).filter(
			(term) => term?.type === "exclude",
		);

		const queryTerms = includeTerms;

		const convertSortOrder = (sortOrder) => {
			if (!sortOrder) return "DESC";
			return sortOrder.toLowerCase() === "asc" ? "ASC" : "DESC";
		};

		const sortByField =
			this.getSortByKey(filters?.sortBy) || "compositeScore";
		const sortOrderField = convertSortOrder(
			filters?.sortOrder || filters?.view,
		);

		const buildPerformanceDataArray = () => {
			const performanceDataArray = [];

			if (filters.customFilters && Array.isArray(filters.customFilters)) {
				filters.customFilters.forEach((customFilter, index) => {
					if (customFilter.column && customFilter.threshold !== undefined) {
						const mappedColumn = this.mapCustomFilterColumn(
							customFilter.column,
						);
						const threshold = parseFloat(customFilter.threshold);

						if (!isNaN(threshold)) {
							const filterData = {
								filterColumn: mappedColumn,
								filterThreshold: threshold,
							};

							performanceDataArray.push(filterData);
						}
					}
				});
			}

			if (performanceDataArray.length === 0) {
				performanceDataArray.push({
					filterColumn: "cprmarketrequests",
					filterThreshold: 10000,
				});
				performanceDataArray.push({
					filterColumn: "impressions",
					filterThreshold: 100,
				});
			}

			return performanceDataArray;
		};

		const getUploadedDomains = () => {
			const uploadedDomains = filters.uploadedDomains || {};
			return {
				includeDomains: uploadedDomains.includeList || [],
				excludeDomains: uploadedDomains.excludeList || [],
			};
		};

		const { includeDomains, excludeDomains } = getUploadedDomains();

		const payload = {
			query: queryTerms.map((term) => ({
				query: term.text || term.query || "",
				threshold: resolveQueryThreshold(term, "DOMAINS"),
			})),
			excludeQuery: excludeTerms.map((term) => ({
				query: term.text || term.query || "",
				threshold: resolveQueryThreshold(term, "DOMAINS"),
			})),
			filters: {
				performance: {
					minViewability:
						parseFloat(
							filters.minViewability / 100 || filters.viewability / 100,
						) || 0,
					minReach:
						this.convertReachToNumber(
							filters.minReach || filters.reach || filters.expectedReach,
						) || 0,
					performanceData: buildPerformanceDataArray(),
				},
				domainFilters: {
					geography: this.getGeographyArray(
						filters.selectedCountries || filters.geography,
					),
					contentStyle: filters.contentStyle || [],
					videoplcmt: filters.videoPlacement || [],
					distributionChannel: filters.distributionChannel || [],
					deviceTypes: filters.deviceTypes || [],
					mediaSubtype: filters.mediaSubtype || [],
					publisher: Array.isArray(filters.publishers)
						? filters.publishers
						: [],
					isDirect: filters.isDirect || null,
				},
				vectorFilters: {
					safetyTier: filters.safetyTier || [],
					content_style: filters.contentStyle || [],
					iab_category: filters.iab_category || filters.categories || [],
					tier_Details: {
						tier: 0,
						keys: [],
					},
					categoryThreshold: {
						iab_category_score:
							this.convertPercentageToDecimal(filters.relevanceThreshold) ||
							0.75,
						safety_tier_cscore: 0,
						content_style_cscore: 0,
					},
				},
				startIndex: 0,
				noOfResult: filters.limit || 1000,
				noOfDays: this.getPeriodDays(filters),
				cumulativeDataFilters: {
					perfomanceData:
						this.getCumulativeDataKey(performanceMetric) ||
						"avg_clickthroughrate",
					...this.getThresholdValues(filters),
				},
				includeDomains: includeDomains,
				excludeDomains: excludeDomains,
			},
			sortBy: sortByField,
			view: sortOrderField,
		};
		console.log(payload);
		return payload;
	}

	// ==================== HELPER METHODS ====================

	convertPercentageToDecimal(value) {
		if (!value) return 0;
		return typeof value === "string" ? parseFloat(value) / 100 : value / 100;
	}

	convertReachToNumber(reachValue) {
		if (!reachValue) return 0;
		if (typeof reachValue === "string") {
			const value = reachValue.toLowerCase();
			if (value.includes("m")) return parseFloat(value) * 1000000;
			if (value.includes("k")) return parseFloat(value) * 1000;
			return parseFloat(value) || 0;
		}
		return parseFloat(reachValue) || 0;
	}

	getGeographyArray(geography) {
		if (!geography || geography === "Global") return [];
		if (Array.isArray(geography)) return geography.length > 0 ? geography : [];
		if (typeof geography === "string") return [geography];
		return [];
	}

	getPeriodDays(filters) {
		if (filters.period !== undefined) {
			const periodNum = parseInt(filters.period);
			if (!isNaN(periodNum) && periodNum > 0) {
				return periodNum;
			}
		}

		if (filters.dateRange !== undefined) {
			const dateRangeNum = parseInt(filters.dateRange);
			if (!isNaN(dateRangeNum) && dateRangeNum > 0) {
				return dateRangeNum;
			}
		}

		return 30;
	}

	getThresholdValues(filters) {
		const thresholdType = filters.thresholdType || filters.type || "reach";
		const minReachDay =
			filters.minReachDay ||
			filters.reachThreshold ||
			filters.minReachThreshold ||
			1;
		const reachThresholdValue = parseFloat(minReachDay) * 1000000000;

		if (thresholdType === "market_request" || thresholdType === "market") {
			return {
				reachThreashold: 0,
				marketThreashold: reachThresholdValue,
			};
		} else {
			return {
				reachThreashold: reachThresholdValue,
				marketThreashold: 0,
			};
		}
	}

	getSortByKey(option) {
		const mapping = {
			totalImpressions: "totalImpressions",
			domainReach: "domainReach",
			avgViewability: "avgViewability",
			calculated_ctr: "calculated_ctr",
			domainName: "domainName",
			totalCprMarketRequests: "totalCprMarketRequests",
			compositeScore: "compositeScore",
		};
		return mapping[option] || "totalImpressions";
	}

	mapCustomFilterColumn(uiColumn) {
		const columnMapping = {
			cprmarketrequests: "cprmarketrequests",
			impressions: "impressions",
			clicks: "clicks",
			viewables: "viewables",
			viewabilitymeasures: "viewabilitymeasures",
			completefirstoccurrence: "completefirstoccurrence",
			startfirstoccurrence: "startfirstoccurrence",
		};

		return columnMapping[uiColumn] || uiColumn;
	}

	mapUIFiltersToBackend(uiFilters) {
		const mapping = {
			Display: "Display",
			"Display+Native": "Display",
			"Display+Video": "Video",
			Native: "Native",
			Video: "Video",
			desktop: "desktop",
			mobile: "mobile",
			tablet: "tablet",
			Site: "Site",
			App: "App",
		};

		const mapArray = (arr) => {
			if (!Array.isArray(arr)) return [];
			return arr.map((item) => mapping[item] || item);
		};

		return {
			...uiFilters,
			mediaSubtype: mapArray(uiFilters.mediaSubtype),
			deviceTypes: mapArray(uiFilters.deviceTypes),
			distributionChannel: mapArray(uiFilters.distributionChannel),
			contentStyle: mapArray(uiFilters.contentStyle),
			safetyTier: mapArray(uiFilters.safetyTier),
		};
	}

	getCumulativeDataKey(metric) {
		const mapping = {
			viewability: "avg_viewability",
			ctr: "avg_clickthroughrate",
			vtr: "avg_vtr",
			reach: "total_cprmarketrequests",
			impressions: "total_impressions",
		};
		return mapping[metric] || "avg_clickthroughrate";
	}
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;
